// src/components/FishIQPilotCard.tsx
import React, { useMemo, useState } from 'react';

import type { UserProfile } from '../types';
import { updateUserProfile } from '../services/users';
import { getRoddoUiTokens } from '../constants/theme';

const FISHIQ_PILOT_CONSENT_VERSION = 'fishiq-pilot-consent-v1';

const normalizePartnerCode = (value: string) =>
  value
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9-_]/g, '')
    .slice(0, 32);

interface FishIQPilotCardProps {
  userProfile: UserProfile | null;
  isDarkMode: boolean;
  addToast?: (message: string, type?: any) => void;
}

const FishIQPilotCard: React.FC<FishIQPilotCardProps> = ({ userProfile, isDarkMode, addToast }) => {
  const ui = getRoddoUiTokens(isDarkMode);
  const pilot = (userProfile as any)?.fishiqPilotEnrollment || {};
  const legalConsent = (userProfile as any)?.legal?.consents?.fishiqContribution?.enabled === true;
  const currentlyEnrolled = pilot?.enrolled === true || (userProfile as any)?.fishiqPilotEnrolled === true;

  // Keep Pilot available without letting enrollment/setup dominate the core FishIQ flow.
  // Anglers can expand it intentionally when they want to review or change participation.
  const initialOpen = false;
  const [isExpanded, setIsExpanded] = useState(initialOpen);
  const [partnerCode, setPartnerCode] = useState<string>(
    String(pilot?.partnerCode || (userProfile as any)?.fishiqPilotPartnerCode || ''),
  );
  const [isSaving, setIsSaving] = useState(false);

  const dataPromiseRows = useMemo(
    () => [
      'Exact fishing spots stay private in your own catch diary.',
      'Pilot exports use generalized regions and aggregate counts only.',
      'No raw GPS, photos, names, or private catch notes are included in exports.',
      'You can keep any catch private-only by turning off fishing-data contribution when logging.',
      'Roddo Reads are educational and probabilistic; regulations and safe access come first.',
    ],
    [],
  );

  const shownPartnerCode = normalizePartnerCode(partnerCode) || 'RODDO-PILOT';

  const savePilotEnrollment = async (nextEnrolled: boolean) => {
    if (!userProfile?.uid || isSaving) return;

    setIsSaving(true);

    const now = Date.now();
    const cleanPartnerCode = normalizePartnerCode(partnerCode);

    try {
      const previousLegal = ((userProfile as any)?.legal || {}) as any;
      const previousConsents = previousLegal?.consents || {};

      await updateUserProfile(userProfile.uid, {
        fishiqContributionEnabled: nextEnrolled,
        fishiqPilotEnrolled: nextEnrolled,
        fishiqPilotPartnerCode: cleanPartnerCode || null,
        fishiqPilotCohortCode: cleanPartnerCode || 'RODDO-PILOT',
        fishiqPilotEnrollment: {
          ...(pilot || {}),
          enrolled: nextEnrolled,
          status: nextEnrolled ? 'enrolled' : 'paused',
          partnerCode: cleanPartnerCode || null,
          cohortCode: cleanPartnerCode || 'RODDO-PILOT',
          consentVersion: FISHIQ_PILOT_CONSENT_VERSION,
          enrolledAt: nextEnrolled ? (pilot?.enrolledAt || now) : pilot?.enrolledAt || null,
          pausedAt: nextEnrolled ? null : now,
          updatedAt: now,
        },
        legal: {
          ...previousLegal,
          consents: {
            ...previousConsents,
            fishiqContribution: {
              ...(previousConsents?.fishiqContribution || {}),
              enabled: nextEnrolled,
              version: FISHIQ_PILOT_CONSENT_VERSION,
              updatedAt: now,
              scope: 'generalized_aggregate_research_observations',
            },
          },
        },
      } as any);

      addToast?.(
        nextEnrolled
          ? 'Roddo Pilot is on. Future opted-in logs can support privacy-safe area learning.'
          : 'Roddo Pilot is paused. Future logs stay private-only unless you opt in again.',
        'success',
      );

      if (nextEnrolled) setIsExpanded(false);
    } catch (err: any) {
      console.error('[FishIQPilotCard] enrollment save failed', err);
      addToast?.(err?.message || 'Could not update Roddo Pilot enrollment.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section
      className="relative overflow-hidden rounded-[20px] p-3.5 sm:p-4"
      style={{
        background: isDarkMode
          ? '#101827'
          : '#FFFCF7',
        border: `1px solid ${isDarkMode ? 'rgba(122,232,184,0.20)' : 'rgba(80,180,140,0.28)'}`,
        boxShadow: isDarkMode
          ? '0 10px 24px rgba(0,0,0,0.20), inset 0 1px 0 rgba(148,166,201,0.08)'
          : '0 8px 20px rgba(89,65,37,0.07), inset 0 1px 0 rgba(255,255,255,0.88)',
      }}
    >
      <button
        type="button"
        onClick={() => setIsExpanded((v) => !v)}
        className="absolute right-3.5 top-3.5 grid h-9 w-9 place-items-center rounded-full text-sm font-black transition hover:-translate-y-0.5 active:translate-y-px sm:right-4 sm:top-4"
        style={{
          background: isDarkMode ? 'rgba(148,166,201,0.07)' : 'rgba(255,255,255,0.78)',
          border: `1px solid ${ui.border}`,
          color: ui.textPrimary,
        }}
        aria-label={isExpanded ? 'Collapse Roddo pilot card' : 'Expand Roddo pilot card'}
      >
        {isExpanded ? '−' : '+'}
      </button>

      <div className="pr-12">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className="inline-flex rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-[0.16em]"
            style={{
              background: currentlyEnrolled
                ? 'rgba(122,232,184,0.18)'
                : isDarkMode
                  ? 'rgba(148,166,201,0.07)'
                  : 'rgba(255,255,255,0.78)',
              color: currentlyEnrolled ? (isDarkMode ? '#BDF7DE' : '#176C50') : ui.textSecondary,
              border: `1px solid ${currentlyEnrolled ? 'rgba(122,232,184,0.32)' : ui.border}`,
            }}
          >
            {currentlyEnrolled ? 'Pilot enrolled' : 'Roddo pilot mode'}
          </span>

          {(legalConsent || currentlyEnrolled) && (
            <span
              className="rounded-full px-2.5 py-1 text-[9.5px] font-black"
              style={{
                background: 'rgba(122,232,184,0.14)',
                color: isDarkMode ? '#BDF7DE' : '#176C50',
                border: '1px solid rgba(122,232,184,0.20)',
              }}
            >
              Consent v1
            </span>
          )}
        </div>

        <h2 className="mt-2 text-[17px] font-black leading-tight" style={{ color: ui.textPrimary }}>
          Privacy-first Roddo Pilot
        </h2>

        <p className="mt-1 max-w-[560px] text-[12px] font-semibold leading-relaxed" style={{ color: ui.textSecondary }}>
          Private spots stay private. Pilot reports use generalized, aggregate-only area signals.
        </p>

        {currentlyEnrolled && (
          <p className="mt-1 text-[11px] font-black uppercase tracking-[0.12em]" style={{ color: ui.textSecondary }}>
            Cohort: {shownPartnerCode}
          </p>
        )}
      </div>

      {isExpanded && (
        <div className="mt-4">
          <div
            className="rounded-[16px] p-3"
            style={{
              background: isDarkMode ? '#0F1828' : 'rgba(255,255,255,0.74)',
              border: `1px solid ${ui.border}`,
            }}
          >
            <div className="text-[11px] font-black uppercase tracking-[0.16em]" style={{ color: ui.textSecondary }}>
              Roddo Data Promise
            </div>

            <div className="mt-2 space-y-1.5">
              {dataPromiseRows.map((row) => (
                <div
                  key={row}
                  className="flex items-start gap-2 rounded-[12px] px-2.5 py-2 text-[11.5px] font-semibold leading-relaxed"
                  style={{
                    background: isDarkMode ? '#0C1423' : 'rgba(255,253,248,0.82)',
                    color: ui.textSecondary,
                    border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.07)' : 'rgba(207,184,151,0.28)'}`,
                  }}
                >
                  <span aria-hidden="true" className="mt-0.5 shrink-0">
                    •
                  </span>
                  <span>{row}</span>
                </div>
              ))}
            </div>
          </div>

          <label
            className="mt-3 block rounded-[16px] p-3"
            style={{
              background: isDarkMode ? 'rgba(103,216,232,0.06)' : 'rgba(234,247,255,0.78)',
              border: `1px solid ${isDarkMode ? 'rgba(124,199,255,0.16)' : 'rgba(124,199,255,0.28)'}`,
            }}
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-[11px] font-black uppercase tracking-[0.16em]" style={{ color: ui.textSecondary }}>
                Partner / cohort code
              </span>

              <button
                type="button"
                disabled={!userProfile?.uid || isSaving}
                onClick={(e) => {
                  e.preventDefault();
                  void savePilotEnrollment(!currentlyEnrolled);
                }}
                className="rounded-[14px] px-3 py-2 text-[10px] font-black uppercase tracking-[0.12em] transition hover:-translate-y-0.5 active:translate-y-px disabled:opacity-60"
                style={{
                  background: currentlyEnrolled
                    ? isDarkMode
                      ? '#182238'
                      : '#F8F2E9'
                    : isDarkMode ? '#8A70F2' : '#2F80ED',
                  color: currentlyEnrolled ? ui.textPrimary : '#FFFFFF',
                  border: `1px solid ${currentlyEnrolled ? ui.border : 'rgba(30,120,92,0.25)'}`,
                  boxShadow: currentlyEnrolled ? 'none' : (isDarkMode ? '0 8px 18px rgba(44,32,110,0.22)' : '0 8px 18px rgba(47,128,237,0.18)'),
                }}
              >
                {isSaving ? 'Saving...' : currentlyEnrolled ? 'Pause' : 'Join'}
              </button>
            </div>

            <input
              value={partnerCode}
              onChange={(e) => setPartnerCode(normalizePartnerCode(e.target.value))}
              placeholder="e.g. SHOP-01"
              className="mt-2 w-full rounded-[16px] px-3 py-2 text-sm font-black outline-none"
              style={{
                background: isDarkMode ? '#0C1423' : 'rgba(255,255,255,0.92)',
                color: ui.textPrimary,
                border: `1px solid ${ui.border}`,
              }}
            />

            <span className="mt-2 block text-[11.5px] font-semibold leading-relaxed" style={{ color: ui.textSecondary }}>
              Use this for charter captains, tackle shops, clubs, or Sea Grant cohorts. Leave blank for the default Roddo pilot.
            </span>
          </label>

          <div className="mt-3 px-1">
            <h3 className="text-[16px] font-black leading-tight" style={{ color: ui.textPrimary }}>
              Privacy-first pilot participation
            </h3>
            <p className="mt-1 text-[12.5px] font-semibold leading-relaxed" style={{ color: ui.textSecondary }}>
              Help Roddo build community-powered, privacy-safe fishing intelligence. Participation is optional and catch-by-catch.
            </p>
          </div>
        </div>
      )}
    </section>
  );
};

export default FishIQPilotCard;